import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  RealtimeKitProvider,
  useRealtimeKitClient,
} from '@cloudflare/realtimekit-react';
import {
  RtkMeeting,
  provideRtkDesignSystem,
  RtkUiBuilder,
} from '@cloudflare/realtimekit-react-ui';
import type {
  TokenRequest,
  TokenResponse,
  TokenErrorResponse,
  CreateRoomResponse,
  AdminTokenRequest,
  AdminActionRequest,
  JoinPhase,
} from './types';
import {
  AUDIO_CONFIG,
  applyShareMode,
  setAudioBitrate,
  setScreenshareAudioBitrate,
  setBroadcastSdpActive,
  SCREENSHARE_AUDIO_BITRATE,
  type AudioPreset,
  type ShareMode,
} from './audio';
import { setNoiseCancellation } from './noiseCancellation';
import { watchMicLevel, type MicLevel } from './micLevel';
import { SettingsPanel } from './SettingsPanel';
import { Lobby } from './Lobby';

const TOGGLES_TO_REMOVE = [
  'rtk-livestream-toggle',
  'rtk-polls-toggle',
  'rtk-plugins-toggle',
  'rtk-ai-toggle',
  'rtk-recording-toggle',
  'rtk-breakout-rooms-toggle',
] as const;

const CONTROLBAR_REGIONS = [
  'div#controlbar-left',
  'div#controlbar-center',
  'div#controlbar-right',
  'div#controlbar-mobile',
  'rtk-more-toggle',
] as const;

// Matches the app's own stack in index.css — all locally installed, nothing fetched.
const FONT_STACK = 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif';

/**
 * A dropped connection throws a TypeError out of `fetch`, whose message
 * ("Failed to fetch" / "Load failed") is engine-specific and means nothing to a
 * visitor. Everything else is either our own thrown message from the Worker or
 * an unknown, which gets the caller's fallback.
 */
function joinErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof TypeError) return 'Couldn’t reach the server. Try again.';
  return err instanceof Error && err.message ? err.message : fallback;
}

function buildLobbyConfig() {
  const builder = new RtkUiBuilder();
  for (const region of CONTROLBAR_REGIONS) {
    const node = builder.find(region);
    if (!node) continue;
    for (const toggle of TOGGLES_TO_REMOVE) {
      node.remove(toggle);
    }
  }
  const config = builder.build();
  // The UI kit's default design tokens set `googleFont: 'Inter'`, and
  // <RtkMeeting> feeds them to provideRtkDesignSystem on mount — which injects
  // <link> tags to fonts.googleapis.com + fonts.gstatic.com. That is the only
  // non-Cloudflare destination the app contacts, and it hands Google every
  // visitor's IP, User-Agent and Referer for a font we don't need. The loader
  // only runs when `googleFont` is a string, so dropping the key stops the
  // request outright; `fontFamily` keeps the UI on the local stack.
  const tokens = { ...(config.designTokens ?? {}), fontFamily: FONT_STACK };
  delete (tokens as { googleFont?: string }).googleFont;
  config.designTokens = tokens;
  return config;
}

export function App() {
  // resetOnLeave clears `meeting` (→ undefined) on roomLeft, which returns us to
  // the lobby. The SDK excludes 'connected-meeting'/'disconnected' states, so
  // reconnects survive; a preset switch's leave() also clears it briefly,
  // covered by the "switching" placeholder below.
  const [meeting, initMeeting] = useRealtimeKitClient({ resetOnLeave: true });
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preset, setPreset] = useState<AudioPreset>('voice');
  const [bitrateBps, setBitrateBps] = useState(AUDIO_CONFIG.voice.defaultBitrate);
  const [switching, setSwitching] = useState(false);
  const [screensharing, setScreensharing] = useState(false);
  const [shareMode, setShareMode] = useState<ShareMode>('detail');
  const [ncEnabled, setNcEnabled] = useState(true); // noise cancellation on by default (Voice)
  const [ncMessage, setNcMessage] = useState<string | null>(null);
  const [mic, setMic] = useState<MicLevel>({ level: 0, silent: false });
  const [isAdmin, setIsAdmin] = useState(false);
  const [roomLocked, setRoomLocked] = useState(false);
  const [lockedRoomId, setLockedRoomId] = useState<string | null>(null);
  const [passphraseError, setPassphraseError] = useState<string | null>(null);
  // How far the in-flight join has got, so the pressed room row can narrate it.
  const [phase, setPhase] = useState<JoinPhase>(null);
  const tokenRef = useRef<string | null>(null);
  // The participant record behind tokenRef — sent as keepParticipantId when
  // locking so the admin does not revoke their own token.
  const participantIdRef = useRef<string | null>(null);
  const roomIdRef = useRef<string | null>(null);
  const adminPasswordRef = useRef<string | null>(null);
  const presetRef = useRef<AudioPreset>('voice');
  const bitrateRef = useRef(AUDIO_CONFIG.voice.defaultBitrate);
  const shareModeRef = useRef<ShareMode>('detail');
  const screensharingRef = useRef(false);
  const ncRef = useRef(true);
  const ncBusyRef = useRef(false);
  const lobbyConfig = useMemo(buildLobbyConfig, []);

  useEffect(() => {
    provideRtkDesignSystem(document.documentElement, {
      theme: 'darkest',
      fontFamily: FONT_STACK, // never `googleFont` — see buildLobbyConfig

      colors: {
        brand: {
          300: '#4c8dff',
          400: '#3a7dff',
          500: '#2670ff',
          600: '#1a5fe8',
          700: '#114bbf',
        },
      },
    });
  }, []);

  // Re-apply live audio bitrate after each (re)join — producer params reset on
  // rejoin, and preset switches re-init the meeting. Also track screenshare
  // state to show the Detail/Motion control and apply the mode on share start.
  useEffect(() => {
    if (!meeting) return;
    const onRoomJoined = () => {
      void setAudioBitrate(meeting, bitrateRef.current);
      // NC is on by default (and middleware is dropped on re-init, e.g. preset
      // switch), so re-apply it on every join. After a Music switch ncRef is
      // false, so this no-ops.
      if (ncRef.current) {
        void setNoiseCancellation(meeting, true).then((ok) => {
          // This is an automatic attempt (not a user click), so on failure
          // (Safari/WebKit, WASM blocked) fall back to Off SILENTLY — don't nag
          // with a note on every join. A manual toggle still surfaces the reason.
          if (!ok) {
            ncRef.current = false;
            setNcEnabled(false);
          }
        });
      }
    };
    const onScreenShareUpdate = ({
      screenShareEnabled,
    }: {
      screenShareEnabled: boolean;
    }) => {
      setScreensharing(screenShareEnabled);
      screensharingRef.current = screenShareEnabled;
      if (screenShareEnabled) {
        void applyShareMode(meeting, shareModeRef.current);
        // Shared tab/system audio — lift its bitrate so Opus stays fullband.
        // In Broadcast it tracks the selected bitrate; otherwise a fixed default.
        const ssBitrate =
          presetRef.current === 'broadcast' ? bitrateRef.current : SCREENSHARE_AUDIO_BITRATE;
        void setScreenshareAudioBitrate(meeting, ssBitrate);
      }
    };
    meeting.self.on('roomJoined', onRoomJoined);
    meeting.self.on('screenShareUpdate', onScreenShareUpdate);
    if (meeting.self.roomJoined) onRoomJoined();
    return () => {
      meeting.self.off('roomJoined', onRoomJoined);
      meeting.self.off('screenShareUpdate', onScreenShareUpdate);
    };
  }, [meeting]);

  // Live mic input meter + "mic appears silent" hint. Keyed on `meeting` for
  // the usual reason: every preset switch / admin unlock builds a new client.
  useEffect(() => {
    if (!meeting) return;
    return watchMicLevel(meeting, setMic);
  }, [meeting]);

  const initWithPreset = useCallback(
    async (token: string, p: AudioPreset) => {
      presetRef.current = p;
      // Broadcast forces true stereo Opus via an SDP rewrite (the SDK's
      // enableStereo alone negotiates mono on the encoder). Set before init so
      // the new transport's offer/answer is rewritten.
      setBroadcastSdpActive(p === 'broadcast');
      const { defaultBitrate: _defaultBitrate, ...audioConstraints } = AUDIO_CONFIG[p];
      await initMeeting({
        authToken: token,
        defaults: {
          audio: true,
          video: true,
          mediaConfiguration: {
            audio: audioConstraints,
          },
        },
      });
    },
    [initMeeting],
  );

  // A genuine leave clears `meeting` (resetOnLeave) → we fall back to the lobby.
  // Reset the join spinner so the lobby is interactive again, and drop admin
  // elevation — it's per-room and per-session, so a new room starts unelevated.
  // A preset switch / admin unlock also blips `meeting` to undefined, but those
  // hold `switching` true across the re-init, so we skip the reset there (else
  // switching presets or the unlock itself would immediately clear admin state).
  useEffect(() => {
    if (!meeting && !switching) {
      setSubmitting(false);
      setPhase(null);
      setIsAdmin(false);
      setRoomLocked(false);
      adminPasswordRef.current = null;
    }
  }, [meeting, switching]);

  // Mint a room-scoped token and join. The token carries the room, so preset
  // switching (which re-inits via tokenRef) keeps you in the same room.
  const mintAndJoin = useCallback(
    async (
      roomId: string,
      displayName: string,
      passphrase?: string,
    ): Promise<'locked' | null> => {
      const body: TokenRequest = { displayName, roomId, passphrase };
      setPhase('token');
      const res = await fetch('/api/token', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as TokenErrorResponse;
        setPhase(null);
        // Locked room: signal up so the lobby can prompt for the passphrase.
        if (res.status === 403 && err.locked) return 'locked';
        throw new Error(err.error ?? `Server error (${res.status})`);
      }
      const data = (await res.json()) as TokenResponse;
      tokenRef.current = data.token;
      participantIdRef.current = data.participantId;
      roomIdRef.current = roomId;
      setPhase('connect');
      await initWithPreset(data.token, preset);
      return null;
    },
    [preset, initWithPreset],
  );

  const handleJoin = useCallback(
    async (roomId: string, passphrase?: string) => {
      const trimmed = name.trim();
      if (!trimmed || submitting) return;
      setSubmitting(true);
      setError(null);
      setPassphraseError(null);
      try {
        const result = await mintAndJoin(roomId, trimmed, passphrase);
        if (result === 'locked') {
          // Prompt for the passphrase inline on this room's row. A wrong
          // passphrase comes back 'locked' again — surface the mismatch.
          if (passphrase) setPassphraseError('Incorrect passphrase');
          setLockedRoomId(roomId);
          setSubmitting(false);
        } else {
          // The server only lets a passphrase-less join through for an unlocked
          // room, so needing one is the authoritative signal the room is locked —
          // seed roomLocked so an admin sees Unlock (not Lock) for a pre-locked
          // room they just joined / reloaded into.
          setRoomLocked(Boolean(passphrase));
        }
      } catch (err) {
        setError(joinErrorMessage(err, 'Couldn’t get you in. Try again.'));
        setPhase(null);
        setSubmitting(false);
      }
    },
    [name, submitting, mintAndJoin],
  );

  const handleCreate = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/rooms', { method: 'POST' });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as TokenErrorResponse;
        throw new Error(err.error ?? `Server error (${res.status})`);
      }
      const data = (await res.json()) as CreateRoomResponse;
      await mintAndJoin(data.id, trimmed);
      setRoomLocked(false); // a freshly created room is never locked
    } catch (err) {
      setError(joinErrorMessage(err, 'Couldn’t create a room. Try again.'));
      setPhase(null);
      setSubmitting(false);
    }
  }, [name, submitting, mintAndJoin]);

  // Clearing both is what makes Cancel on an expanded locked row work at all:
  // `lockedRoomId` is set on a 403 and was previously never cleared anywhere, so
  // the row stayed expanded for the life of the lobby.
  const dismissLock = useCallback(() => {
    setLockedRoomId(null);
    setPassphraseError(null);
  }, []);

  // Preset changes affect capture-time DSP/stereo, so they require a
  // leave → re-init. The preset's default bitrate is re-applied on rejoin.
  const switchPreset = useCallback(
    async (next: AudioPreset) => {
      if (switching || !meeting || !tokenRef.current || next === presetRef.current) {
        return;
      }
      const nextBitrate = AUDIO_CONFIG[next].defaultBitrate;
      setSwitching(true);
      try {
        try {
          await meeting.leave();
        } catch {
          // ignore — proceeding to re-init regardless
        }
        bitrateRef.current = nextBitrate;
        setBitrateBps(nextBitrate);
        // Never denoise the stereo presets (music/broadcast) — force NC off so
        // the roomJoined re-apply won't re-add the middleware after the re-init.
        if (next !== 'voice') {
          ncRef.current = false;
          setNcEnabled(false);
        }
        await initWithPreset(tokenRef.current, next);
        setPreset(next);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Preset switch failed');
      } finally {
        setSwitching(false);
      }
    },
    [switching, meeting, initWithPreset],
  );

  const handleBitrateChange = useCallback(
    (bps: number) => {
      bitrateRef.current = bps;
      setBitrateBps(bps);
      if (!meeting) return;
      void setAudioBitrate(meeting, bps);
      // In Broadcast the bitrate control also governs the shared tab/system
      // audio producer — apply it live while sharing (no rejoin).
      if (presetRef.current === 'broadcast' && screensharingRef.current) {
        void setScreenshareAudioBitrate(meeting, bps);
      }
    },
    [meeting],
  );

  const handleShareModeChange = useCallback(
    (next: ShareMode) => {
      shareModeRef.current = next;
      setShareMode(next);
      if (meeting) void applyShareMode(meeting, next);
    },
    [meeting],
  );

  // Live NC toggle. Serialized via ncBusyRef so rapid On/Off can't interleave
  // two middleware rebuilds, and the UI only commits "On" once the SDK confirms
  // it applied (it returns success:false — without throwing — on WebKit/Safari
  // or if the worklet/wasm fails to load).
  const handleNcChange = useCallback(
    async (enabled: boolean) => {
      if (!meeting || ncBusyRef.current) return;
      ncBusyRef.current = true;
      setNcMessage(null);
      setNcEnabled(enabled); // optimistic; reverted below on failure
      try {
        const ok = await setNoiseCancellation(meeting, enabled);
        if (enabled && !ok) {
          ncRef.current = false;
          setNcEnabled(false);
          setNcMessage('Noise cancellation isn’t available in this browser.');
        } else {
          ncRef.current = enabled;
        }
      } finally {
        ncBusyRef.current = false;
      }
    },
    [meeting],
  );

  // Elevate the current session to host: swap in a host-preset token and re-init
  // the meeting with it (same leave→init blip as switchPreset, so roomJoined
  // re-applies bitrate/NC on the new host connection). Returns an error string
  // on failure; null on success.
  const unlockAdmin = useCallback(
    async (password: string): Promise<string | null> => {
      if (!roomIdRef.current) return 'Not in a room';
      const body: AdminTokenRequest = {
        password,
        roomId: roomIdRef.current,
        displayName: name,
      };
      const res = await fetch('/api/admin/token', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as TokenErrorResponse;
        return err.error ?? `Server error (${res.status})`;
      }
      const data = (await res.json()) as TokenResponse;
      tokenRef.current = data.token;
      participantIdRef.current = data.participantId;
      adminPasswordRef.current = password;
      setIsAdmin(true);
      setSwitching(true);
      try {
        try {
          await meeting?.leave();
        } catch {
          // ignore — proceeding to re-init regardless
        }
        await initWithPreset(tokenRef.current, presetRef.current);
      } finally {
        setSwitching(false);
      }
      return null;
    },
    [name, meeting, initWithPreset],
  );

  const lockRoom = useCallback(async (passphrase: string): Promise<string | null> => {
    if (!roomIdRef.current || !adminPasswordRef.current) return 'Not authorized';
    const body: AdminActionRequest = {
      password: adminPasswordRef.current,
      roomId: roomIdRef.current,
      passphrase,
      keepParticipantId: participantIdRef.current ?? undefined,
    };
    const res = await fetch('/api/admin/lock', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as TokenErrorResponse;
      return err.error ?? `Server error (${res.status})`;
    }
    setRoomLocked(true);
    return null;
  }, []);

  const unlockRoom = useCallback(async (): Promise<string | null> => {
    if (!roomIdRef.current || !adminPasswordRef.current) return 'Not authorized';
    const body: AdminActionRequest = {
      password: adminPasswordRef.current,
      roomId: roomIdRef.current,
    };
    const res = await fetch('/api/admin/unlock', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as TokenErrorResponse;
      return err.error ?? `Server error (${res.status})`;
    }
    setRoomLocked(false);
    return null;
  }, []);

  if (meeting) {
    return (
      <div className="meeting-shell">
        <RealtimeKitProvider value={meeting}>
          <RtkMeeting
            meeting={meeting}
            mode="fill"
            showSetupScreen
            config={lobbyConfig}
          />
        </RealtimeKitProvider>
        <SettingsPanel
          preset={preset}
          switching={switching}
          onPresetChange={switchPreset}
          bitrateBps={bitrateBps}
          onBitrateChange={handleBitrateChange}
          screensharing={screensharing}
          shareMode={shareMode}
          onShareModeChange={handleShareModeChange}
          ncEnabled={ncEnabled}
          onNcChange={handleNcChange}
          ncMessage={ncMessage}
          mic={mic}
          isAdmin={isAdmin}
          roomLocked={roomLocked}
          onUnlockAdmin={unlockAdmin}
          onLockRoom={lockRoom}
          onUnlockRoom={unlockRoom}
        />
      </div>
    );
  }

  // Mid preset-switch, `meeting` blips to undefined (resetOnLeave) before the
  // re-init lands. Hold this placeholder so the lobby doesn't flash.
  if (switching) {
    return (
      <div className="entry">
        <div className="entry-card">
          <p>Switching audio mode…</p>
        </div>
      </div>
    );
  }

  return (
    <Lobby
      name={name}
      onNameChange={setName}
      onJoin={handleJoin}
      onCreate={handleCreate}
      busy={submitting}
      error={error}
      lockedRoomId={lockedRoomId}
      passphraseError={passphraseError}
      phase={phase}
      onDismissLock={dismissLock}
    />
  );
}
